import { usePage } from '@inertiajs/react';
import axios from 'axios';
import { useEffect, useState } from 'react';

interface CacheEntry<T> {
    data?: T;
    promise?: Promise<T>;
}

/**
 * Module-level cache shared across every component that calls {@link useCachedFetch}.
 * It survives Inertia page swaps (the SPA never reloads the module), so re-opening a
 * page or a filter modal serves the previously fetched data synchronously — no refetch,
 * no spinner, no pop-in.
 *
 * Entries are keyed by `<auth user id>|<url>`. Impersonation redirects via
 * `redirect()->back()`, which Inertia follows as an SPA visit rather than a full reload,
 * so a URL-only key would let an impersonated session read responses fetched under the
 * previous identity. Scoping by identity forces a refetch under the new user's permissions.
 */
const cache = new Map<string, CacheEntry<unknown>>();

/** Build the identity-scoped cache key for a URL. */
function cacheKeyFor(identity: string, url: string): string {
    return `${identity}|${url}`;
}

/**
 * Drop a cached endpoint for every identity (e.g. after a mutation that changes it),
 * or the whole cache when called with no URL.
 */
export function invalidateCachedFetch(url?: string): void {
    if (!url) {
        cache.clear();
        return;
    }

    for (const key of [...cache.keys()]) {
        if (key.endsWith(`|${url}`)) {
            cache.delete(key);
        }
    }
}

/**
 * GET a URL once and cache the JSON response for the lifetime of the SPA session.
 * Concurrent callers for the same URL share a single in-flight request. A warm cache
 * hit returns instantly with `loading = false`; a cold call exposes `loading = true`
 * so the UI can show a spinner instead of hiding content until the data arrives.
 *
 * Pass `null` as the URL to skip fetching (e.g. when the caller lacks permission).
 */
export function useCachedFetch<T = unknown>(url: string | null): { data: T | undefined; loading: boolean } {
    const page = usePage<{ auth?: { user?: { id?: string | number } | null } }>();
    const identity = String(page.props.auth?.user?.id ?? 'guest');
    const key = url ? cacheKeyFor(identity, url) : null;

    const readCache = (): T | undefined => (key ? (cache.get(key)?.data as T | undefined) : undefined);

    const [data, setData] = useState<T | undefined>(readCache);
    const [loading, setLoading] = useState<boolean>(() => key != null && readCache() === undefined);

    useEffect(() => {
        if (!url || !key) {
            setData(undefined);
            setLoading(false);
            return;
        }

        let active = true;
        const entry = cache.get(key);

        // Warm cache — serve synchronously, no spinner.
        if (entry?.data !== undefined) {
            setData(entry.data as T);
            setLoading(false);
            return;
        }

        setData(undefined);
        setLoading(true);

        // Reuse an in-flight request if one is already running for this key, otherwise start one.
        const request =
            (entry?.promise as Promise<T> | undefined) ??
            axios.get<T>(url).then((response) => {
                cache.set(key, { data: response.data });
                return response.data;
            });

        if (!entry?.promise) {
            cache.set(key, { promise: request as Promise<unknown> });
        }

        request
            .then((result) => {
                if (active) {
                    setData(result);
                    setLoading(false);
                }
            })
            .catch(() => {
                // Forget the failed request so a later mount can retry.
                if (cache.get(key)?.data === undefined) {
                    cache.delete(key);
                }
                if (active) {
                    setLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [key, url]);

    return { data, loading };
}
